import { createFileRoute } from '@tanstack/react-router';
import Head from 'next/head';
import Script from 'next/script';
import { useEffect, useState, useRef, FormEvent } from 'react';
import { motion, type Variants } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Phone,
  Mail,
  MessageSquare,
  MapPin,
  Clock,
  Send,
  Building2,
  Headphones,
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  HelpCircle,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { LandingTopBar } from '@/featured/landing-page/components/LandingTopBar';
import { LandingFooter } from '@/featured/landing-page/components/LandingFooter';

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const CONTACT_INFO = [
  {
    icon: Phone,
    label: 'Office Phone',
    value: '021 2787 4648',
    href: 'tel:+622127874648',
    subtext: 'Official office telephone line',
    actionText: 'Call Office',
  },
  {
    icon: MessageSquare,
    label: 'Sales Support (WhatsApp)',
    value: '+62 813-8260-9869',
    href: 'https://wa.me/6281382609869',
    subtext: 'Direct sales chat & product inquiry',
    actionText: 'Chat Sales',
  },
  {
    icon: Mail,
    label: 'Email Support',
    value: 'support@mitos.id',
    href: 'mailto:support@mitos.id',
    subtext: 'We usually respond within 24 hours',
    actionText: 'Send Email',
  },
  {
    icon: MessageSquare,
    label: 'Technical Support (WhatsApp)',
    value: '0896 3455 5083',
    href: 'https://wa.me/6289634555083',
    subtext: 'Direct chat with tech support team',
    actionText: 'Chat on WhatsApp',
  },
];

const SERVICES_OPTIONS = [
  'GPS Tracking Web Based Software',
  'Iridium EdgeSolar Terminal',
  'Globalstar Solar Cell',
  'Skywave & Orbcomm SC1000',
  'Missi Asset Monitor (MAM)',
  'RPM & Hour Meter Sensor',
  'Seamless Infrastructure Integration',
  'General Inquiry / Other',
];

const SUPPORT_CHANNELS = [
  {
    icon: Headphones,
    title: '24/7 Fleet Technical Support',
    description:
      'Engineers and marine specialists ready to assist with terminal configurations, AIS feeds, and signal troubleshooting.',
  },
  {
    icon: Building2,
    title: 'Enterprise & Sales Inquiries',
    description:
      'Custom fleet tracking plans, hardware bulk ordering, and bespoke system integration tailored to your operations.',
  },
  {
    icon: ShieldCheck,
    title: 'Authorized Hardware Supply',
    description:
      'Official authorized distributor of Skywave Orbcomm, Globalstar, and Iridium Edge Solar terminals in Indonesia.',
  },
  {
    icon: HelpCircle,
    title: 'Onboarding & Training',
    description:
      'Complete onboarding sessions for vessel managers, captains, and maritime dispatch operators.',
  },
];

function ContactPage() {
  useEffect(() => {
    document.documentElement.setAttribute('dir', 'ltr');
  }, []);

  return (
    <>
      <Head>
        <title>Contact — Missi Track</title>
        <meta
          name="description"
          content="Get in touch with Missi Track for real-time vessel tracking, satellite communication solutions, and fleet management support."
        />
        <meta property="og:title" content="Contact — Missi Track" />
        <meta
          property="og:description"
          content="Contact Missi Track sales, technical support, and satellite communications team in Jakarta, Indonesia."
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Archivo+Black&family=Inter:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />
      </Head>
      <main className="landing min-h-screen">
        <LandingTopBar />
        <Hero />
        <ContactFormSection />
        <SupportChannels />
        <LandingFooter />
      </main>
    </>
  );
}

ContactPage.guestGuard = false;
ContactPage.authGuard = false;

export default ContactPage;

export const Route = createFileRoute('/contact')({
  component: ContactPage,
});

/* ---------------- Hero ---------------- */
function Hero() {
  return (
    <section className="px-4 pt-24 md:pt-32 pb-6">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial="hidden"
          animate="show"
          variants={stagger}
          className="relative overflow-hidden rounded-[2rem] bg-ink p-8 text-white md:p-14"
          style={{
            backgroundImage: `linear-gradient(135deg, oklch(0.18 0.02 240 / 0.88), oklch(0.10 0.02 240 / 0.94)), url('/images/jumbotron.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <motion.div variants={fadeUp}>
            <span className="pill bg-white/10 text-white shadow-none">
              GET IN TOUCH
            </span>
          </motion.div>
          <motion.h1
            variants={fadeUp}
            className="display mt-6 text-[12vw] font-black leading-[0.85] tracking-tight md:text-[7.5rem]"
          >
            CONTACT US
          </motion.h1>
          <motion.p
            variants={fadeUp}
            className="mt-6 max-w-2xl text-base text-white/70 md:text-lg"
          >
            Have questions about our vessel tracking systems, satellite hardware, or fleet management platform? Our maritime technology team is here to assist you.
          </motion.p>

          <motion.div
            variants={fadeUp}
            className="mt-10 grid gap-4 sm:grid-cols-3"
          >
            {CONTACT_INFO.map((item) => (
              <a
                key={item.label}
                href={item.href}
                target={item.href.startsWith('http') ? '_blank' : undefined}
                rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur transition hover:bg-white/10 hover:border-white/20"
              >
                <div className="flex items-center justify-between">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-white/10 text-white group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <item.icon className="h-5 w-5" />
                  </span>
                  <ExternalLink className="h-4 w-4 text-white/40 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-white/60">
                  {item.label}
                </div>
                <div className="mt-1 text-base font-bold text-white group-hover:text-primary transition-colors">
                  {item.value}
                </div>
                <div className="mt-1 text-xs text-white/50">{item.subtext}</div>
              </a>
            ))}
          </motion.div>

          <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-primary/30 blur-3xl" />
        </motion.div>
      </div>
    </section>
  );
}

interface TurnstileWidgetProps {
  siteKey: string;
  onVerify: (token: string) => void;
  onExpire?: () => void;
}

function CloudflareTurnstileWidget({ siteKey, onVerify, onExpire }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const renderTurnstile = () => {
    if (typeof window !== 'undefined' && (window as any).turnstile && containerRef.current) {
      if (widgetIdRef.current) {
        try {
          (window as any).turnstile.remove(widgetIdRef.current);
        } catch (e) {}
      }
      widgetIdRef.current = (window as any).turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token: string) => onVerify(token),
        'expired-callback': () => onExpire?.(),
        'error-callback': () => onExpire?.(),
        theme: 'auto',
      });
    }
  };

  useEffect(() => {
    renderTurnstile();
    return () => {
      if (widgetIdRef.current && typeof window !== 'undefined' && (window as any).turnstile) {
        try {
          (window as any).turnstile.remove(widgetIdRef.current);
        } catch (e) {}
      }
    };
  }, [siteKey]);

  return (
    <>
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
        onLoad={renderTurnstile}
        strategy="afterInteractive"
      />
      <div ref={containerRef} className="my-2 flex justify-center" />
    </>
  );
}

/* ---------------- Contact Form & Location Section ---------------- */
function ContactFormSection() {
  const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
  const siteKey = turnstileSiteKey || recaptchaSiteKey;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: SERVICES_OPTIONS[0],
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [captcha, setCaptcha] = useState<{ question: string; hash: string } | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const fetchCaptcha = async () => {
    if (siteKey) return; // Math CAPTCHA not needed if Cloudflare / ReCAPTCHA site key is present
    try {
      const res = await fetch('/api/captcha');
      const data = await res.json();
      if (data?.question) {
        setCaptcha(data);
        setCaptchaAnswer('');
      }
    } catch (err) {
      console.error('Failed to fetch CAPTCHA:', err);
    }
  };

  useEffect(() => {
    fetchCaptcha();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields.');
      return;
    }

    if (siteKey && !captchaToken) {
      toast.error('Please complete the Cloudflare Security Verification.');
      return;
    }

    if (!siteKey && !captchaAnswer.trim()) {
      toast.error('Please complete the Security Verification (CAPTCHA).');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          captchaToken,
          captchaAnswer,
          captchaHash: captcha?.hash,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to send message.');
      }

      setIsSuccess(true);
      toast.success('Thank you! Your message has been sent successfully.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: SERVICES_OPTIONS[0],
        message: '',
      });
      setCaptchaAnswer('');
      setCaptchaToken(null);
    } catch (err: any) {
      toast.error(err?.message || 'An error occurred while submitting your message.');
    } finally {
      setIsSubmitting(false);
      fetchCaptcha();
    }
  };

  return (
    <section className="px-4 py-20 md:py-28 bg-background">
      <div className="mx-auto max-w-7xl">
        <div className="grid items-start gap-12 lg:grid-cols-12">
          {/* Left Column: Form */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
            className="card-surface p-8 md:p-12 lg:col-span-7"
          >
            <motion.div variants={fadeUp}>
              <span className="pill text-primary">SEND A MESSAGE</span>
              <h2 className="display mt-4 text-3xl font-black md:text-5xl text-foreground">
                HOW CAN WE <span className="text-primary">HELP YOU?</span>
              </h2>
              <p className="mt-3 text-sm text-muted-foreground">
                Fill out the form below and our maritime support team will reach out to you as soon as possible.
              </p>
            </motion.div>

            {isSuccess ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-8 rounded-2xl bg-primary/10 border border-primary/20 p-8 text-center"
              >
                <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-primary text-primary-foreground">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <h3 className="mt-4 text-xl font-bold text-foreground">Message Sent!</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Thank you for reaching out. A representative from Missi Track will get back to you shortly.
                </p>
                <button
                  onClick={() => setIsSuccess(false)}
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
                >
                  Send another message
                </button>
              </motion.div>
            ) : (
              <motion.form
                variants={fadeUp}
                onSubmit={handleSubmit}
                className="mt-8 grid gap-5 sm:grid-cols-2"
              >
                <div className="flex flex-col gap-2">
                  <label htmlFor="name" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Full Name <span className="text-primary">*</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    required
                    placeholder="Captain John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm font-medium text-foreground placeholder:text-muted-foreground/30 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Email Address <span className="text-primary">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    placeholder="name@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm font-medium text-foreground placeholder:text-muted-foreground/30 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="phone" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Phone / WhatsApp
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    placeholder="+62 812 3456 7890"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm font-medium text-foreground placeholder:text-muted-foreground/30 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="company" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Company Name
                  </label>
                  <input
                    id="company"
                    type="text"
                    placeholder="PT Maritime Shipping"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm font-medium text-foreground placeholder:text-muted-foreground/30 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>

                <div className="sm:col-span-2 flex flex-col gap-2">
                  <label htmlFor="service" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Service of Interest
                  </label>
                  <select
                    id="service"
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm font-medium text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    {SERVICES_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2 flex flex-col gap-2">
                  <label htmlFor="message" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Message <span className="text-primary">*</span>
                  </label>
                  <textarea
                    id="message"
                    required
                    rows={4}
                    placeholder="Describe your fleet tracking needs or inquiries..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm font-medium text-foreground placeholder:text-muted-foreground/30 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                  />
                </div>

                {/* Security Verification / CAPTCHA */}
                {turnstileSiteKey ? (
                  <div className="sm:col-span-2 flex flex-col items-center justify-center p-3 rounded-2xl border border-input bg-secondary/20">
                    <CloudflareTurnstileWidget
                      siteKey={turnstileSiteKey}
                      onVerify={(token) => setCaptchaToken(token)}
                      onExpire={() => setCaptchaToken(null)}
                    />
                  </div>
                ) : recaptchaSiteKey ? (
                  <div className="sm:col-span-2 flex flex-col items-center justify-center p-3 rounded-2xl border border-input bg-secondary/20">
                    <ReCAPTCHA
                      sitekey={recaptchaSiteKey}
                      onChange={(token) => setCaptchaToken(token)}
                      onExpired={() => setCaptchaToken(null)}
                    />
                  </div>
                ) : (
                  <div className="sm:col-span-2 flex flex-col gap-2 rounded-2xl border border-input bg-secondary/30 p-4">
                    <div className="flex items-center justify-between">
                      <label htmlFor="captcha" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Security Verification (CAPTCHA) <span className="text-primary">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={fetchCaptcha}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline transition"
                      >
                        <RefreshCw className="h-3 w-3" /> Refresh
                      </button>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <div className="flex shrink-0 items-center justify-center rounded-xl bg-ink px-4 py-2.5 text-sm font-bold text-white shadow-inner font-mono tracking-wider select-none">
                        {captcha?.question || 'Loading...'}
                      </div>
                      <input
                        id="captcha"
                        type="text"
                        required
                        placeholder="Answer..."
                        value={captchaAnswer}
                        onChange={(e) => setCaptchaAnswer(e.target.value)}
                        className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm font-medium text-foreground placeholder:text-muted-foreground/30 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                  </div>
                )}

                <div className="sm:col-span-2 pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-8 py-3.5 text-sm font-semibold text-primary-foreground shadow-lg transition hover:opacity-90 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      'Sending Message...'
                    ) : (
                      <>
                        <Send className="h-4 w-4" /> Send Message
                      </>
                    )}
                  </button>
                </div>
              </motion.form>
            )}
          </motion.div>

          {/* Right Column: Office Location & Details */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
            className="flex flex-col gap-6 lg:col-span-5"
          >
            <motion.div variants={fadeUp} className="card-surface p-8">
              <h3 className="text-xl font-black text-foreground">HEAD OFFICE</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Visit our headquarters or contact our support desk in Jakarta.
              </p>

              <div className="mt-6 flex flex-col gap-5">
                <div className="flex items-start gap-4">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-secondary text-primary">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Office Address
                    </div>
                    <div className="mt-1 text-sm font-semibold text-foreground leading-snug">
                      Perkantoran Tanjung Mas Raya, blok B1 nomor 44, RT.002/RW.001. Tanjung Barat, Jagakarsa, Jakarta Selatan.
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-secondary text-primary">
                    <Clock className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Operating Hours
                    </div>
                    <div className="mt-1 text-sm font-semibold text-foreground">
                      Monday – Friday: 08:30 – 17:30 WIB
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Closed on Saturdays, Sundays, & National Holidays
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-secondary text-primary">
                    <Phone className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Direct Hotline
                    </div>
                    <div className="mt-1 text-sm font-semibold text-foreground">
                      <a
                        href="tel:+622127874648"
                        className="hover:text-primary transition"
                      >
                        021 2787 4648 (Office)
                      </a>{' '}
                      /{' '}
                      <a
                        href="https://wa.me/6281382609869"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary transition"
                      >
                        +62 813-8260-9869 (Sales)
                      </a>{' '}
                      /{' '}
                      <a
                        href="https://wa.me/6289634555083"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary transition"
                      >
                        0896 3455 5083 (Support)
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Embedded Google Map */}
            <motion.div variants={fadeUp} className="card-surface overflow-hidden p-2">
              <iframe
                title="Missi Track Office Map"
                src="https://maps.google.com/maps?q=Perkantoran+Tanjung+Mas+Raya+Tanjung+Barat+Jagakarsa+Jakarta+Selatan&t=&z=15&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="280"
                style={{ border: 0, borderRadius: '1rem' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Support Channels ---------------- */
function SupportChannels() {
  return (
    <section className="px-4 pb-24 bg-background">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-10 flex flex-wrap items-end justify-between gap-4"
        >
          <div>
            <span className="pill text-primary">OUR COMMITMENT</span>
            <h2 className="display mt-4 text-3xl font-black md:text-5xl text-foreground">
              SUPPORTED BY <span className="text-primary">EXPERTS</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm text-muted-foreground">
            Dedicated support streams to ensure seamless vessel monitoring and satellite connectivity worldwide.
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={stagger}
          className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
        >
          {SUPPORT_CHANNELS.map((ch) => (
            <motion.div
              key={ch.title}
              variants={fadeUp}
              className="card-surface group flex h-full flex-col p-6 hover-lift"
            >
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-secondary text-primary transition group-hover:bg-primary group-hover:text-primary-foreground">
                <ch.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-foreground">{ch.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{ch.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
